/**
 * AuthCallback — the `/auth/callback` landing screen for the RCI-Auth broker
 * OIDC round-trip.
 *
 * The broker redirects here with `?code=…&state=…` after the user authenticates.
 * This screen exchanges the code for tokens (PKCE, in-browser — see brokerAuth),
 * then navigates into the app. It renders a minimal branded "signing you in…"
 * state while the exchange runs, and a retry affordance if it fails. It lives
 * OUTSIDE the AppShell (see router) because there is no session yet.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { completeLogin, beginLogin } from "@api/brokerAuth";

type Phase = "exchanging" | "error";

export function AuthCallback() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("exchanging");
  const [message, setMessage] = useState<string>("");
  // StrictMode double-invokes effects in dev; guard so we exchange the code once.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await completeLogin(window.location.search);
        // Land in the app; replace so the callback URL (with the code) is not
        // left in history.
        navigate("/wells", { replace: true });
      } catch (err) {
        setMessage(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    })();
  }, [navigate]);

  return (
    <div className="auth-callback">
      <div className="auth-callback__card">
        <img className="auth-callback__logo" src="/olympic-logo.png" alt="OLYMPIC" />
        <div className="auth-callback__wordmark">OLYMPIC</div>
        {phase === "exchanging" ? (
          <p className="auth-callback__status">Signing you in…</p>
        ) : (
          <>
            <p className="auth-callback__status auth-callback__status--error">
              Sign-in could not be completed.
            </p>
            <p className="auth-callback__detail">{message}</p>
            <button
              type="button"
              className="btn"
              onClick={() => {
                void beginLogin();
              }}
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
